import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

type BudgetGroupUserPagedResponse = components['schemas']['GetUsersByBudgetGroupEndpoint_BudgetGroupUserPagedResponse'];

export interface UseUsersByBudgetGroupReturn extends BaseQuery {
  data: BudgetGroupUserPagedResponse | null;
}

export function useUsersByBudgetGroup(
  budgetGroupId: number | undefined,
  page: number = 1,
  pageSize: number = 10,
): UseUsersByBudgetGroupReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getUsersByBudgetGroup = async () => {
    if (!budgetGroupId || budgetGroupId <= 0 || !organizationId) {
      return {
        items: [],
        totalCount: 0,
        page: 1,
        pageSize: pageSize,
        totalPages: 0,
      } as BudgetGroupUserPagedResponse;
    }

    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/budget-groups/{budgetGroupId}/users', {
      params: {
        path: { organizationId, budgetGroupId },
        query: { Page: page, PageSize: pageSize },
      },
    });

    return data as BudgetGroupUserPagedResponse;
  };

  const query = useQuery({
    queryKey: ['usersByBudgetGroup', organizationId, budgetGroupId, page, pageSize],
    queryFn: getUsersByBudgetGroup,
    enabled: !!budgetGroupId && budgetGroupId > 0 && organizationId > 0,
    retry: false,
    refetchOnWindowFocus: true,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = query;

  return {
    data: data ?? null,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
